/**
 * TS collapsible header (B36): "Department bar stays".
 *
 * Desktop (1000px and up): the announcement and the dark header scroll away and the department bar stays at the top
 * in a compact form, with a Search button and the cart. Search slides the whole header back down and puts the cursor
 * in the store search; scrolling on down slides it away again.
 * Phones and tablets: the whole header scrolls away and a slim bar (Shop, Search, Quick Order, Cart) stays at the top.
 * Shop slides the header back down and opens the menu; Search opens a search box in the bar.
 *
 * How: the header section is sticky (theme setting). Its top is set to minus --ts-hd-off, the height of the part that
 * should scroll away, so the section sticks with only the department bar showing (phones: none of it).
 * --header-height is kept equal to what is actually pinned at the top, so the sticky add to cart bar, the sticky
 * filters and the department dropdown line up under the bar.
 * Without JavaScript the header behaves as the theme's sticky header.
 *
 * The phone bar is moved out of the header to the end of <body>: a bar fixed to the screen inside the sticky header
 * can be drawn in the wrong place on iPhones once the header has scrolled away (found 2026-10-13: on product pages
 * the add to cart bar showed with a gap above it).
 *
 * 2026-10-14 (B39): where the header starts is measured from the element above it in the page flow only. A fixed or
 * absolutely placed element in front of the header (the theme's loading bar, or something an app or browser extension
 * adds) moves with the screen, so measuring it pushed the dock point down with every scroll and the header never
 * docked: the dark header scrolled away, the full department bar stayed, and --header-height stayed at the full
 * header height, so the add to cart bar showed with a gap above it. Also checked again on pageshow (back/forward).
 *
 * 2026-10-15 (B40): start collapsed. On the pages listed in snippets/ts-hdock-mobile.liquid (the Graveyard Shift game)
 * <html> gets ts-hd-start while the page loads. Then the header is docked from the start at any scroll position: the
 * other header group sections (announcement bar) get ts-hds-hide, and the header section (ts-hds-wrap) is pulled up by
 * --ts-hd-pull, the part that would have scrolled away (desktop: the dark header and the docked bar's top padding;
 * phones: the header minus the slim bar's height, which the slim bar covers). Its sticky top is the same amount, so it
 * stays put; peek still sets top 0 and slides the whole header down over the page. A click outside the header while
 * it is down, or closing the phone menu that Shop opened, slides it away again, so the game can be played without
 * scrolling.
 *
 * 2026-10-21 (B43): the sidebar column. While sections/ts-sidebar.liquid shows its column (1000px and wider, section
 * on), the departments live in the column and the header is a white top bar with no department bar to dock to. So on
 * desktop nothing docks: the whole header section stays pinned at top 0 as the theme's sticky header, --ts-hd-off is 0
 * and --header-height is the top bar's height. Phones and tablets keep the slim bar as before.
 *
 * 2026-10-23 (B46): phones fire resize all the time (the address bar slides in and out while scrolling, the keyboard
 * opens when a search box takes the cursor). Every resize used to undock and dock again, which closed the slim bar's
 * search box the moment the keyboard opened (Nick: the Search button on the collapsed bar would not open the search),
 * and the theme's own resize handler set --header-height back to the full header for a frame, so the sticky add to
 * cart bar under the slim bar jumped down and back (Nick: the bar was jumping around the screen). Now --header-height
 * is put back straight away on every resize, and the dock is measured again from scratch only when the width changes.
 *
 * 2026-10-24: widening the window from phone to desktop size while the header was docked set --header-height to
 * -18px for a moment: the header was still marked docked from the phone layout, and on desktop with the sidebar column
 * the department bar is hidden (0px tall) but its docked top padding (18px) was still taken off. The sticky add to cart
 * bar turned that into an invalid observer margin ("--18px") and threw an error. pinned() now treats the sidebar layout
 * as never docked and never returns less than 0.
 */
(function () {
  'use strict';

  var DESK = window.matchMedia('(min-width: 1000px)');
  var REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)');
  var PEEK_HIDE_AFTER = 40; // px scrolled down before a slid-down header goes away again
  var html = document.documentElement;

  var section = null;  // <section data-ts-hd> inside the header
  var wrap = null;     // #shopify-section-... (the sticky element)
  var header = null;   // the dark header
  var dbar = null;     // the department bar (desktop)
  var dbarInner = null;
  var mbar = null;     // the slim bar (phones)
  var msearch = null;  // its search box
  var docked = false;
  var peek = false;
  var peekY = 0;
  var ticking = false;
  var countObserver = null;
  var START = false;   // B40: this page starts with the header collapsed (html.ts-hd-start)
  var dockPad = 18;    // the docked desktop bar's top padding (ts-hdock.css), measured while docked
  var menuObserver = null;

  function toArray(list) {
    return Array.prototype.slice.call(list);
  }

  // B43: the sidebar column is on screen (sections/ts-sidebar.liquid shows it 1000px and wider). Then nothing docks.
  function sidebarOn() {
    var aside = document.querySelector('.ts-sb');
    return !!aside && window.getComputedStyle(aside).display !== 'none';
  }

  // Docked, the department bar keeps its height (so the page never moves) but its content sits in the lower part
  // (ts-hdock.css adds a top padding). That top strip scrolls away with the dark header.
  function dockExtra() {
    if (!docked || peek || !DESK.matches || !dbarInner) return 0;
    return parseFloat(window.getComputedStyle(dbarInner).paddingTop) || 0;
  }

  // Height of the part that scrolls away before the section sticks.
  function offset() {
    if (sidebarOn()) return 0;
    if (START) return pull();
    if (DESK.matches) return (header ? header.offsetHeight : 0) + dockExtra();
    return wrap.offsetHeight;
  }

  // B40: the slim bar's own height (its search box closed).
  function mbarHeight() {
    var row = mbar && mbar.querySelector('.ts-hdm__row');
    if (!row) return 56;
    return row.offsetHeight + (parseFloat(window.getComputedStyle(mbar).borderBottomWidth) || 0);
  }

  // B40: how far a start-collapsed header section is pulled up. It does not change while the header is slid down, so
  // the page under it never moves.
  function pull() {
    if (!START || !wrap || sidebarOn()) return 0;
    if (DESK.matches) {
      var pad = dockExtra();
      if (pad > 0) dockPad = pad;
      return (header ? header.offsetHeight : 0) + dockPad;
    }
    return Math.max(0, wrap.offsetHeight - mbarHeight());
  }

  // Scroll position where the dark header (phones: the whole header) has gone off the top.
  function dockPoint() {
    return naturalTop() + (DESK.matches ? (header ? header.offsetHeight : 0) : wrap.offsetHeight);
  }

  // A box that takes up room in the page flow above the header. Fixed and absolutely placed elements do not: a fixed one
  // also moves with the screen, so measuring it would move the dock point with every scroll (B39).
  function inFlow(el) {
    if (!(el.offsetHeight > 0 || el.getClientRects().length)) return false;
    var position = window.getComputedStyle(el).position;
    return position !== 'fixed' && position !== 'absolute';
  }

  // Where the section starts on the page when it is not stuck (under the announcement bar).
  function naturalTop() {
    var prev = wrap.previousElementSibling;
    while (prev) {
      if (inFlow(prev)) {
        return prev.getBoundingClientRect().bottom + window.pageYOffset;
      }
      prev = prev.previousElementSibling;
    }
    return 0;
  }

  // What is pinned at the top of the screen right now.
  function pinned() {
    if (!docked || peek || sidebarOn()) return wrap.offsetHeight;
    if (DESK.matches) return dbar ? Math.max(0, dbar.offsetHeight - dockExtra()) : 0;
    return mbar ? mbar.offsetHeight : 0;
  }

  function setVars() {
    html.style.setProperty('--ts-hd-pull', pull() + 'px');
    html.style.setProperty('--ts-hd-off', offset() + 'px');
    html.style.setProperty('--header-height', pinned() + 'px');
  }

  function syncBar() {
    if (!mbar) return;
    var shown = docked && !peek && !DESK.matches;
    mbar.setAttribute('aria-hidden', shown ? 'false' : 'true');
    if (!shown) closeSearch(false);
  }

  function setDocked(on) {
    if (on === docked) return;
    docked = on;
    html.classList.toggle('ts-hd-docked', on);
    if (!on && peek) setPeek(false);
    syncBar();
    setVars();
  }

  function setPeek(on) {
    peek = on;
    peekY = window.pageYOffset;
    html.classList.toggle('ts-hd-peek', on);
    syncBar();
    setVars();
  }

  // Something in the slid-down header is in use: keep it down.
  function busy() {
    var active = document.activeElement;
    if (active && wrap.contains(active) && active.matches && active.matches('input, select, textarea')) return true;
    if (section.querySelector('[data-action="toggle-menu"][aria-expanded="true"]')) return true;
    if (section.querySelector('.search-bar.is-expanded')) return true;
    if (!DESK.matches && section.querySelector('.header__search-bar-wrapper.is-visible')) return true;
    return false;
  }

  function update() {
    ticking = false;
    if (!wrap) return;
    var y = window.pageYOffset;
    var point = dockPoint();
    setDocked(!sidebarOn() && (START || (point > naturalTop() && y >= point - 1)));
    if (peek) {
      if (y < peekY) peekY = y;
      else if (y > peekY + PEEK_HIDE_AFTER && !busy()) setPeek(false);
    }
  }

  function onScroll() {
    if (!ticking) {
      ticking = true;
      window.requestAnimationFrame(update);
    }
  }

  // B46: only a width change (turning the phone, resizing a desktop window) can change the header's size. A height change
  // is the phone's address bar or keyboard, so it must not undock: undocking closes the slim bar's search box.
  var lastWidth = window.innerWidth;

  function onResize() {
    if (!wrap) return;
    // The theme (theme.js, HeaderSection) sets --header-height to the full header height on every resize. Put ours back
    // at once, and again before the next frame in case the theme's handler ran after this one (theme editor reloads),
    // so nothing lined up under the header (the sticky add to cart bar) ever jumps (B46).
    setVars();
    window.requestAnimationFrame(setVars);
    var widthChanged = window.innerWidth !== lastWidth;
    lastWidth = window.innerWidth;
    setTimeout(function () {
      if (!wrap) return;
      if (widthChanged) setDocked(false);
      update();
      setVars();
    }, 0);
  }

  // Run fn once the header has slid down.
  function afterSlide(fn) {
    if (REDUCE.matches) {
      fn();
      return;
    }
    var done = false;
    function finish() {
      if (done) return;
      done = true;
      wrap.removeEventListener('transitionend', onEnd);
      fn();
    }
    function onEnd(event) {
      if (event.target === wrap && event.propertyName === 'top') finish();
    }
    wrap.addEventListener('transitionend', onEnd);
    setTimeout(finish, 330);
  }

  function openSearch() {
    if (!msearch) return;
    var button = mbar.querySelector('[data-ts-hd-act="msearch"]');
    msearch.hidden = false;
    if (button) button.setAttribute('aria-expanded', 'true');
    var input = msearch.querySelector('input[name="q"]');
    if (input) input.focus();
    setVars();
  }

  function closeSearch(refresh) {
    if (!msearch || msearch.hidden) return;
    var button = mbar.querySelector('[data-ts-hd-act="msearch"]');
    msearch.hidden = true;
    if (button) button.setAttribute('aria-expanded', 'false');
    if (refresh !== false) setVars();
  }

  function act(kind, event) {
    if (kind === 'cart') {
      // Same as the header cart icon: opens the cart drawer.
      var toggle = section.querySelector('.header__cart-toggle');
      if (toggle) {
        event.preventDefault();
        toggle.click();
      }
      return;
    }

    if (kind === 'msearch') {
      event.preventDefault();
      if (msearch && msearch.hidden) openSearch();
      else closeSearch();
      return;
    }

    event.preventDefault();
    var run = function () {
      if (kind === 'menu') {
        var menuToggle = section.querySelector('[data-action="toggle-menu"]');
        if (menuToggle && menuToggle.getAttribute('aria-expanded') !== 'true') menuToggle.click();
      } else if (kind === 'search') {
        var input = header && (header.querySelector('.search-bar__input') || header.querySelector('.search-bar input[name="q"]'));
        if (input) {
          try { input.focus({ preventScroll: true }); } catch (e) { input.focus(); }
        }
      }
    };
    if (docked && !peek) {
      setPeek(true);
      afterSlide(run);
    } else {
      run();
    }
  }

  function onClick(event) {
    var trigger = event.target.closest && event.target.closest('[data-ts-hd-act]');
    if (!trigger || !section) return;
    if (!section.contains(trigger) && !(mbar && mbar.contains(trigger))) return;
    act(trigger.getAttribute('data-ts-hd-act'), event);
  }

  // B40: on a start-collapsed page, a click outside the slid-down header (into the game) slides it away again once
  // nothing in it is in use. Checked a moment later so the click has moved the focus and closed any open menu first.
  function onPointerDown(event) {
    if (!START || !peek || !wrap) return;
    var target = event.target;
    if (wrap.contains(target) || (mbar && mbar.contains(target))) return;
    setTimeout(function () {
      if (peek && !busy()) setPeek(false);
    }, 60);
  }

  // B40: on a start-collapsed page, closing the phone menu (its X, the dim area, Escape) slides the header away again.
  function watchMenu() {
    if (menuObserver) menuObserver.disconnect();
    menuObserver = null;
    var toggle = START && section.querySelector('[data-action="toggle-menu"]');
    if (!toggle || !window.MutationObserver) return;
    menuObserver = new MutationObserver(function () {
      if (toggle.getAttribute('aria-expanded') === 'true' || !peek) return;
      setTimeout(function () {
        if (peek && !busy()) setPeek(false);
      }, 60);
    });
    menuObserver.observe(toggle, { attributes: true, attributeFilter: ['aria-expanded'] });
  }

  function onKeydown(event) {
    if (event.key !== 'Escape') return;
    if (msearch && !msearch.hidden) {
      closeSearch();
      var button = mbar.querySelector('[data-ts-hd-act="msearch"]');
      if (button) button.focus();
    }
  }

  // Keep the bar counts equal to the header cart count.
  function watchCount() {
    if (countObserver) countObserver.disconnect();
    var source = section.querySelector('.header__cart-count');
    var targets = toArray(section.querySelectorAll('[data-ts-hd-count]'));
    if (mbar && !section.contains(mbar)) targets = targets.concat(toArray(mbar.querySelectorAll('[data-ts-hd-count]')));
    if (!source || !targets.length || !window.MutationObserver) return;
    var copy = function () {
      var value = (source.textContent || '').trim();
      targets.forEach(function (target) { target.textContent = value; });
    };
    countObserver = new MutationObserver(copy);
    countObserver.observe(source, { childList: true, characterData: true, subtree: true });
    copy();
  }

  // Keep one phone bar, at the end of <body> (a theme editor reload renders a new one inside the header).
  function moveBar(bar) {
    toArray(document.querySelectorAll('[data-ts-hdm]')).forEach(function (other) {
      if (other !== bar && other.parentNode) other.parentNode.removeChild(other);
    });
    if (bar && bar.parentNode !== document.body) document.body.appendChild(bar);
  }

  function init() {
    section = document.querySelector('[data-ts-hd]');
    if (!section) {
      html.classList.remove('ts-hd-on', 'ts-hd-docked', 'ts-hd-peek');
      moveBar(null);
      wrap = null;
      mbar = null;
      return;
    }
    wrap = section.closest('.shopify-section') || section.parentElement;
    header = section.querySelector('.header');
    dbar = section.querySelector('.ts-db');
    dbarInner = dbar && dbar.querySelector('.ts-db__bar-inner');
    mbar = section.querySelector('[data-ts-hdm]') || document.querySelector('body > [data-ts-hdm]');
    moveBar(mbar);
    msearch = mbar && mbar.querySelector('.ts-hdm__search');
    docked = false;
    peek = false;
    START = html.classList.contains('ts-hd-start');
    if (START && !sidebarOn()) {
      wrap.classList.add('ts-hds-wrap');
      toArray(document.querySelectorAll('.shopify-section-group-header-group')).forEach(function (el) {
        if (el !== wrap && !el.contains(wrap)) el.classList.add('ts-hds-hide');
      });
    }
    html.classList.remove('ts-hd-docked', 'ts-hd-peek');
    html.classList.add('ts-hd-on');
    watchCount();
    watchMenu();
    setVars();
    update();
  }

  // Measure again once the page has settled (load) and when it comes back from the back/forward cache (pageshow).
  function refresh() {
    if (!wrap) return;
    setVars();
    update();
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onResize);
  window.addEventListener('load', refresh);
  window.addEventListener('pageshow', refresh);
  document.addEventListener('click', onClick);
  document.addEventListener('pointerdown', onPointerDown, true);
  document.addEventListener('keydown', onKeydown);
  document.addEventListener('shopify:section:load', function () { setTimeout(init, 0); });
  document.addEventListener('shopify:section:unload', function () { setTimeout(init, 0); });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { setTimeout(init, 0); });
  } else {
    setTimeout(init, 0);
  }
})();